import { Scene, _ } from "~/route/globals";
import { node } from "~/util/dom";
import Items, { IItemData } from "~/route/items";
import { ROUTE_CONST } from "~/route/const";
import { getTripPOI } from "~/route/utils";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import { timeWithUserSpeed } from "~/util/user-speed";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { isRouteReadOnly } from "~/route/utils-common";

/**
 * Obal pro:
 * - vylet
 */
export default class ItemTrip {
	_parent: Items;

	_data: IItemData;

	_dom: {
		container: HTMLDivElement;
	};

	_layer: any;

	_saveData: {
		clientX: number;
		percent: number;
	};

	_wasmMarkers: Array<WasmStoreMarker>;

	constructor(parent: Items, data: IItemData) {
		this._parent = parent;
		this._data = data;
		this._dom = {
			container: node("div", { className: "route-item-trip" }),
		};
		this._layer = Mapy.getComponent("masakovac");
		this._saveData = {
			clientX: -1,
			percent: 0,
		};
		this._wasmMarkers = [];

		this._build();
		this._analytics();
	}

	destroy() {
		this._dom.container.remove();

		for (const marker of this._wasmMarkers) {
			marker.destroy();
		}

		this._wasmMarkers.length = 0;
	}

	get container() {
		return this._dom.container;
	}

	get state() {
		return this._data;
	}

	_build() {
		const routeState = Mapy.CML.state.route;
		const routeIndex = routeState.index;
		const title = node("h2", { textContent: `${this._data.title === "" ? Mapy.getComponent("gps").coordsToFormat(Scene.stringToCoords(this._data.geometry)[0]) : this._data.title} ${_("circuit.item.area")}` });
		// cancel trip
		const cancelTrip = node("button", { textContent: _("circuit.item.cancel"), className: "cancel" });

		cancelTrip.addEventListener("click", () => {
			this._cancelTripClick();
		});
		const info = node("p", { className: "info", textContent: this._getInfo(routeIndex) });

		this._dom.container.appendChild(title);
		title.appendChild(cancelTrip);
		this._dom.container.appendChild(info);

		// graf pouze pokud nemam waypointy a nebo index je vetsi nez 0 (pac bug z appek)
		if (!this._data.tripData[routeIndex].tripParams.waypoints && this._data.tripData[routeIndex].tripParams.tripIndex >= 0 && !isRouteReadOnly(routeState.userIsPremium, routeState.params, !!routeState.items[0].planTrip)) {
			this._buildTripGraph(routeIndex);
		}

		// cestou uvidite
		if (this._data.tripData[routeIndex].pois.length) {
			const poisCaption = node("h4", { textContent: _("circuit.pois") });
			const tripsBy = node("ul", { className: "trips-by" });

			this._data.tripData[routeIndex].pois.forEach(data => {
				const { li, wasmMarker } = getTripPOI(data, this._layer);

				tripsBy.appendChild(li);
				this._wasmMarkers.push(wasmMarker);
			});
			this._dom.container.appendChild(poisCaption);
			this._dom.container.appendChild(tripsBy);
		}
	}

	/**
	 * Vytvoreni casti s grafem.
	 */
	_buildTripGraph(routeIndex: number) {
		const tripList = this._data.tripData[routeIndex].triplist;
		const tripIndex = this._data.tripData[routeIndex].tripParams.tripIndex;

		// trip bar
		const setLength = node("h5", { textContent: _("circuit.item.setLength") });
		const tripBarCont = node("div", { className: "trip-bar-cont" });
		const tripBarInfo = node("div", { className: "trip-bar-info" });

		this._dom.container.appendChild(setLength);
		this._dom.container.appendChild(tripBarCont);
		this._dom.container.appendChild(tripBarInfo);
		// trip bar
		const prevBtn = node("button", { className: "step-prev", textContent: "b", disabled: tripIndex === 0 });
		const nextBtn = node("button", { className: "step-next", textContent: "P", disabled: tripIndex === this._data.tripData[routeIndex].triplist.length - 1 });
		const range = node("input", { type: "range", min: "0", max: (this._data.tripData[routeIndex].triplist.length - 1).toString(), value: tripIndex.toString() });

		range.style.setProperty("--range-value", `${100 / this._data.tripData[routeIndex].triplist.length * tripIndex}%`);
		tripBarCont.appendChild(prevBtn);
		tripBarCont.appendChild(range);
		tripBarCont.appendChild(nextBtn);

		// min a maximalni vzdalenost
		const minDistance = node("span", { className: "circuit-bar-min", textContent: standardDistanceFormat(tripList[0].length).string });
		const maxDistance = node("span", { className: "circuit-bar-max", textContent: standardDistanceFormat(tripList[tripList.length - 1].length).string });

		tripBarInfo.appendChild(minDistance);
		tripBarInfo.appendChild(maxDistance);

		// eventy
		prevBtn.addEventListener("click", () => {
			Mapy.CML.state = {
				route: {
					params: {
						tripIndex: tripIndex - 1,
					},
					zoom: true,
				},
			};
		});
		nextBtn.addEventListener("click", () => {
			Mapy.CML.state = {
				route: {
					params: {
						tripIndex: tripIndex + 1,
					},
					zoom: true,
				},
			};
		});
		range.addEventListener("change", () => {
			Mapy.CML.state = {
				route: {
					params: {
						tripIndex: parseInt(range.value),
					},
					zoom: true,
				},
			};
		});
		range.addEventListener("input", () => {
			range.style.setProperty("--range-value", `${100 / this._data.tripData[routeIndex].triplist.length * parseInt(range.value)}%`);
		});
	}

	/**
	 * Ziskani infa ohledne vyletu.
	 */
	_getInfo(routeIndex: number): string {
		let length = "0";
		let time = 0;

		if (this._data.tripData[routeIndex].tripParams.waypoints) {
			length = this._data.tripData[routeIndex].length;
			time = this._data.tripData[routeIndex].time;
		} else {
			let ind = this._data.tripData[routeIndex].tripParams.tripIndex;

			if (ind < 0) {
				ind = 0;
			}
			length = this._data.tripData[routeIndex].triplist[ind].length;
			time = this._data.tripData[routeIndex].triplist[ind].time;
		}

		return `${standardDistanceFormat(length.toString()).string} - ${standardTimeFormat(timeWithUserSpeed(time, this._data.tripData[routeIndex].tripParams.criterion)).string}`;
	}

	/**
	 * Zruseni vyletu.
	 */
	_cancelTripClick() {
		this._data.planTrip = false;
		this._data.tripData = [];
		this._parent.updateState({
			params: {
				criterion: ROUTE_CONST.types.car.criterion[0],
				type: ROUTE_CONST.TYPE_CAR,
				tripIndex: 0, // pro vylet
				tripCriterion: ROUTE_CONST.circuitTypes.trial.def,
				waypoints: "",
			},
		});

		Mapy.getComponent("history").save();
	}

	_analytics() {
		const routeState = Mapy.CML.state.route;
		const data = this._data.tripData[Mapy.CML.state.route.index];

		Mapy.getComponent("logger").log("circuit", {
			routeType: data.tripParams.criterion,
			count: data.totalCount,
			length: data.length,
			poiCount: data.pois.length,
			viaFerrata: routeState.params.allowViaFerrata,
		});
	}
}
